import {Component, EventEmitter, Input, OnInit, Output, TrackByFunction} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    ReferenceBookResponse,
    ReferenceBookType,
    TrendIncidentResponse,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, rxSpinner, Spinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {combineLatest, EMPTY, mergeMap, of} from 'rxjs';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {TrendIncidentForm} from './trend-incident/trend-incident.component';

@Component({
    selector: 'af-trend-incidents-step',
    templateUrl: './trend-incidents-step.component.html',
    styleUrls: ['trend-incidents-step.component.scss'],
    standalone: false
})
export class TrendIncidentsStepComponent implements OnInit {
    @Input() form: FormGroup<TrendIncidentsForm>;
    @Input() indexStartsFrom: number = 0;
    @Input() trendIncidents: TrendIncidentResponse[];
    @Output() nextStep = new EventEmitter<void>();
    @Output() previousStep = new EventEmitter<void>();
    Spinner = Spinner;

    constructor(
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
        private dictionaryHolderService: DictionaryHolderService,
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
    ) {
    }

    get showDeleteButton(): boolean {
        if(!this.authHolderService.hasAccess(['npck-admin'])) {
            return false;
        }
        const count = this.form?.controls?.trendIncidents?.controls?.length ?? 0;

        return count > 1 && !this.Spinner.isShown('initForms');
    }

    ngOnInit(): void {
        if (!this.form.controls?.trendIncidents) {
            this.form.addControl('trendIncidents', this.formBuilder.array([] as FormGroup<TrendIncidentForm>[]));
            this.form.addControl('deletedTrendIncidents', this.formBuilder.array([] as FormGroup<TrendIncidentForm>[]));
            this.trendIncidents.forEach(item => this.initializeForm(item));
        }
    }

    trackByTrendIncidentIndex: TrackByFunction<FormGroup<TrendIncidentForm>> = (index: number) => index;

    deleteTrendIncident(index: number): void {
        const trendIncident = this.trendIncidents[index];
        this.dialogService.openDialog({
            title: 'i18n_af_deleteTrendIncidentDialogTitle',
            subtitle: 'i18n_af_deleteTrendIncidentDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {id: `${trendIncident?.incidentId}`},
        })
            .pipe(
                mergeMap(dialogResult => !dialogResult && EMPTY || of(dialogResult)
                    .pipe(rxSpinner(`deleteTrendIncident_${index}`))),
            )
            .subscribe(() => {
                const deletedTrendIncidentControl = this.form.controls.trendIncidents?.at(index);
                this.trendIncidents.splice(index, 1);
                this.form.controls.deletedTrendIncidents.push(deletedTrendIncidentControl);
                this.form.controls.trendIncidents.removeAt(index);
            });
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.nextStep.emit();
    }

    private initializeForm(trendIncident: TrendIncidentResponse): void {
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.INCIDENT_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
        ]).subscribe(([incidentStatuses, bins]) => {
            const trendIncidentForm = this.buildTrendIncidentForm(trendIncident, incidentStatuses, bins);
            this.form.controls.trendIncidents.push(trendIncidentForm);
        });
    }

    private buildTrendIncidentForm(
        trendIncident: TrendIncidentResponse,
        statuses: ReferenceBookResponse[],
        bins: ReferenceBookResponse[],
    ): FormGroup<TrendIncidentForm> {
        const relatedOrganizations = trendIncident?.relatedOrganizations?.map(orgBin =>
            findValueInDictionary(bins, orgBin, 'name_ru') ?? '-'
        ).join(', ');

        const governmentOrganization= findValueInDictionary(bins, trendIncident?.governmentBin, 'name_ru') ?? '';
        const createdByOrganization= findValueInDictionary(
            bins,
            trendIncident?.incidentCreatedByOrganization,
            'name_ru'
        ) ?? '';
        const status = findValueInDictionary(statuses, trendIncident?.incidentStatus, 'name_ru') ?? '';
        const resolutionStatus = findValueInDictionary(statuses, trendIncident?.incidentStatus, 'name_ru') ?? '';

        const trendIncidentForm = this.formBuilder.group<TrendIncidentForm>({});
        trendIncidentForm.addControl('incidentId', new FormControl(trendIncident?.incidentId, [Validators.required]));
        trendIncidentForm.addControl('relatedOrganizations', new FormControl(relatedOrganizations));
        trendIncidentForm.addControl('governmentOrganization', new FormControl(governmentOrganization));
        trendIncidentForm.addControl('createdByOrganization', new FormControl(createdByOrganization));
        trendIncidentForm.addControl('status', new FormControl(status));
        trendIncidentForm.addControl('resolution', new FormControl(resolutionStatus));
        trendIncidentForm.addControl('createdDate', new FormControl(this.formatDate(trendIncident?.incidentCreatedDate)));

        return trendIncidentForm;
    }

    private formatDate(date: string): string {
        return date ? dayjs(date).format(DATE_TIME_SERVER_FORMAT) : '-';
    }
}

export type TrendIncidentsFormValue = ɵTypedOrUntyped<TrendIncidentsForm, ɵFormGroupRawValue<TrendIncidentsForm>, any>;

export interface TrendIncidentsForm {
    trendIncidents?: FormArray<FormGroup<TrendIncidentForm>>;
    deletedTrendIncidents?: FormArray<FormGroup<TrendIncidentForm>>;
}
